import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { callAxios } from '../hooks/useAxios'
import { Device, Port, TimeString } from '../types/models'
import { useTranslation } from 'react-i18next'
import { RadioSelector } from '../components/atoms/RadioSelector'
import { ScaleLabeling, ProfitData, ReserveFeeDevice } from '../types/charts'
import { MOBILE_SIZE } from '../types/constants'
import { range, countryToTimezone } from '../utils/time'
import { useApp } from '../hooks/useApp'
import { ProfitHistoryChart } from './charts/ProfitHistoryChart'
import { DateTime } from 'luxon'
import { Profits } from '../utils/profits'
import { SmootheningType } from '../utils/series'
import { ReserveFeeTable } from './ReserveFeeTable'

/** The chart scales, plus the monthly view of the reserve compensation after the service fee. */
export type ProfitScale = ScaleLabeling | 'fee'

export interface ProfitHistoryProps {
  device: Device
  port: Port
  /** Called after every fetch with whether the data carries a solar value. */
  onPvValue?: (hasPv: boolean) => void
  /** An FCR battery: offers the view after the service fee. */
  fcr?: boolean
  /** Called when the view after the service fee is entered or left. */
  onFeeMode?: (feeMode: boolean) => void
}

export function ProfitHistory(props: ProfitHistoryProps) {
  const { device, port, onPvValue, fcr, onFeeMode } = props

  const { t } = useTranslation()
  const app = useApp()

  const [fetching, setFetching] = useState(false)
  const [scale, setScale] = useState<ProfitScale>('day')
  const [fees, setFees] = useState<ReserveFeeDevice | null>(null)
  const feeMode = scale === 'fee'
  const steps: ScaleLabeling = feeMode ? 'month' : scale
  const tz = countryToTimezone(device.location?.country?.iso_3166)
  const [minDate, maxDate, fetchDate] = range(steps, null, 10, tz)

  const options = {
    from: minDate as TimeString,
    to: maxDate as TimeString,
    steps,
    smoothening: 'none' as SmootheningType,
    compact: true
  }

  const empty = {
    timestamp: DateTime.fromMillis(0).toUTC().toISO() as TimeString,
    fcr_down_power: 0,
    fcr_down_price: 0,
    fcr_up_power: 0,
    fcr_up_price: 0,
    batt_power: 0,
    spot_saving: 0
  }

  const noData: ProfitData[] = [empty, empty]
  const [profit, setProfit] = useState<ProfitData[]>(noData)

  useEffect(() => {
    setFetching(true)

    const fetchData = async () => {
      const [res, feeRes] = await Promise.all([
        callAxios<ProfitData[]>({
          url: `/profits/${port.id}?from=${fetchDate}&to=${maxDate}&steps=${steps}`,
        }),
        feeMode ? callAxios<ReserveFeeDevice>({ url: `/reserve-fees/${port.id}` }) : Promise.resolve(null)
      ])
      setFees(feeRes && feeRes.success ? feeRes.data : null)

      if (res.success && res.data.length > 0) {
        res.data.sort((a, b) => (a.timestamp === b.timestamp ? 0 : (a.timestamp < b.timestamp ? -1 : 1)))
        if (feeMode) {
          Profits.applyFees(res.data, feeRes && feeRes.success ? feeRes.data.chart : [], tz)
        } else {
          Profits.calculate(res.data, steps)
        }
        setProfit(Profits.fillData(res.data, options, empty))
        onPvValue && onPvValue(Profits.hasPvValue(res.data))
      } else {
        setProfit(noData)
        onPvValue && onPvValue(false)
      }
      setFetching(false)

      return noData
    }

    fetchData().then(() => {
      setFetching(false)
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale])

  const select = (value: string) => {
    if (value !== scale) {
      setProfit([])
      setScale(value as ProfitScale)
      onFeeMode && onFeeMode(value === 'fee')
    }
  }

  return <Box>
    <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: app.mobileWidth < MOBILE_SIZE ? 'column' : 'row', justifyContent: 'center' }}>
        <RadioSelector
          variant={'primary'}
          value={scale}
          disabled={fetching}
          onClick={select}
          values={{
            hour: t('Scale-Hour'),
            day: t('Scale-Day'),
            week: t('Scale-Week'),
            month: t('Scale-Month'),
            year: t('Scale-Year'),
          }}
        />
      </Box>
      {fcr && (
        <RadioSelector
          variant={'primary'}
          value={scale}
          disabled={fetching}
          onClick={select}
          values={{ fee: t('Scale-MonthAfterFee') }}
        />
      )}
    </Box>

    <ProfitHistoryChart
      id={`power-history-${device.id}-${port.id}`}
      spinner={fetching}
      data={profit}
      options={options}
      feeMode={feeMode}
    />

    {feeMode && fees && (
      <ReserveFeeTable sx={{ mt: 3 }} rows={fees.rows} vatRegistered={fees.vat_registered} shared={fees.devices > 1} />
    )}
  </Box>
}
